// Retouren.
//
// Die Erfassung läuft bewusst in drei Schritten: Kunde → Rechnung → Positionen.
// Eine Retoure ohne Rechnung gäbe es sonst als Beleg ohne Gegenbeleg, und der
// Preis wäre frei wählbar. Das Backend erzwingt dieselbe Reihenfolge; hier wird
// sie nur sichtbar gemacht.
import { useCallback, useEffect, useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten, menge, preis } from '../../../gemeinsam/lib/daten';
import { einheitAnzeige } from '../lib/einheiten';
import type { Kunde } from '../../../gemeinsam/lib/typen';
import {
  Auswahl, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner, TarifSperre,
} from '../../../gemeinsam/komponenten/Basis';
import { useSprache } from '../../../gemeinsam/lib/i18n';

type Retoure = {
  id: string;
  customerId: string;
  customerName?: string;
  invoiceId: string | null;
  invoiceNumber?: string | null;
  refundAmount: number;
  reason: string;
  restock?: boolean;
  createdAt: string;
  createdByName: string;
  items: Array<{ name?: string; quantity: number; unitPrice: number; positionIndex?: number }>;
};

type RetourPosition = {
  index: number;
  produktId: string | null;
  bezeichnung: string;
  einheit: string;
  menge: number;
  einzelpreis: number;
  bereitsRetourniert: number;
  retournierbar: number;
};

type RetourRechnung = {
  id: string;
  nummer: string | null;
  datum: string;
  status: string;
  kundeName: string;
  brutto: number;
  positionen: RetourPosition[];
  vollstaendigRetourniert: boolean;
};

const euro = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const datum = (s: string) => new Date(s).toLocaleDateString('de-DE');

export default function Retouren() {
  const { t } = useSprache();
  const [liste, setListe] = useState<Retoure[] | null>(null);
  const [gesperrt, setGesperrt] = useState<{ upgradeAuf?: string } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfassen, setErfassen] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      setListe(await api<Retoure[]>('/returns'));
      setGesperrt(null);
    } catch (e) {
      // 402 heißt: nicht im Tarif enthalten — das ist kein Fehler, sondern
      // eine Tarifgrenze und bekommt eine eigene Darstellung.
      if (e instanceof ApiFehler && e.istTarifgrenze) {
        setGesperrt({ upgradeAuf: e.daten?.upgradeTo as string | undefined });
      } else {
        setFehler(e instanceof ApiFehler ? e.message : 'Retouren konnten nicht geladen werden.');
      }
      setListe([]);
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  // Die Retoure bucht Ware und Guthaben. Der Kunde will darüber aber ein
  // Papier — das ist die Gutschrift. Sie übernimmt die Positionen und bucht
  // KEIN zweites Guthaben, weil die Retoure das bereits getan hat.
  async function gutschriftErstellen(r: Retoure) {
    if (!r.invoiceId) {
      setFehler('Zu dieser Retoure fehlt die Rechnung.');
      return;
    }
    try {
      await api('/belege/gutschrift-zu-rechnung', {
        methode: 'POST',
        daten: {
          rechnungId: r.invoiceId,
          ausRetoureId: r.id,
          grund: r.reason || 'Retoure',
          positionen: r.items
            .filter(p => typeof p.positionIndex === 'number')
            .map(p => ({ index: p.positionIndex, menge: p.quantity })),
        },
      });
      setFehler(null);
      setMeldung('Gutschrift als Entwurf angelegt — unter „Belege → Gutschriften" zum Drucken.');
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Gutschrift konnte nicht angelegt werden.');
    }
  }

  if (liste === null) return <div className="flex justify-center py-16"><Spinner /></div>;

  if (gesperrt) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('retouren.titel')}</h1>
        <TarifSperre was="Das Retouren-Modul" upgradeAuf={gesperrt.upgradeAuf} />
      </div>
    );
  }

  const summe = liste.reduce((s, r) => s + (Number(r.refundAmount) || 0), 0);

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('retouren.titel')}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {liste.length} Vorgänge · {euro(summe)} gutgeschrieben
          </p>
        </div>
        {!erfassen && (
          <Knopf variante="primaer" onClick={() => setErfassen(true)}>{t('retouren.erfassen')}</Knopf>
        )}
      </div>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />

      {erfassen && (
        <Erfassung
          abbrechen={() => setErfassen(false)}
          fertig={async () => { setErfassen(false); await laden(); }}
        />
      )}

      <Karte>
        <KartenKopf titel={t('retouren.alle')} />
        {liste.length === 0 ? (
          <LeerZustand
            titel={t('retouren.leer')}
            text={t('retouren.leerText')}
            aktion={<Knopf variante="primaer" onClick={() => setErfassen(true)}>{t('retouren.erfassen')}</Knopf>}
          />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {liste.map(r => (
              <li key={r.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                <div className="min-w-[180px] flex-1">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {r.customerName || 'Kunde'}
                    {r.invoiceNumber && (
                      <span className="ms-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                        Rechnung {r.invoiceNumber}
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                    {r.items?.length ?? 0} {r.items?.length === 1 ? 'Position' : 'Positionen'}
                    {' · '}{datum(r.createdAt)}
                    {r.createdByName ? ` · ${r.createdByName}` : ''}
                    {r.reason ? ` · ${r.reason}` : ''}
                    {r.restock === false ? ' · nicht eingelagert' : ''}
                  </p>
                </div>
                <span className="font-medium text-emerald-600 dark:text-emerald-400">{euro(r.refundAmount)}</span>
                <Knopf onClick={() => gutschriftErstellen(r)}>{t('retouren.gutschrift')}</Knopf>
              </li>
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Erfassung({ abbrechen, fertig }: { abbrechen: () => void; fertig: () => Promise<void> }) {
  const { t } = useSprache();
  const { daten: kunden } = useDaten<Kunde[]>('/customers');
  const [kundeId, setKundeId] = useState('');
  const [rechnungen, setRechnungen] = useState<RetourRechnung[] | null>(null);
  const [rechnung, setRechnung] = useState<RetourRechnung | null>(null);
  const [mengen, setMengen] = useState<Record<number, string>>({});
  const [grund, setGrund] = useState('');
  const [einlagern, setEinlagern] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  // Schritt 1 → 2: Rechnungen des gewählten Kunden holen.
  async function kundeWaehlen(id: string) {
    setKundeId(id);
    setRechnung(null);
    setRechnungen(null);
    setMengen({});
    setFehler(null);
    if (!id) return;
    try {
      setRechnungen(await api<RetourRechnung[]>(`/returns/rechnungen?kundeId=${encodeURIComponent(id)}`));
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Rechnungen konnten nicht geladen werden.');
      setRechnungen([]);
    }
  }

  // Schritt 2 → 3: Restmengen frisch holen, statt der Listenkopie zu vertrauen —
  // in der Zwischenzeit kann ein Kollege bereits etwas zurückgenommen haben.
  async function rechnungWaehlen(r: RetourRechnung) {
    setFehler(null);
    try {
      const frisch = await api<RetourRechnung>(`/returns/rechnung/${r.id}`);
      setRechnung(frisch);
      setMengen({});
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Rechnung konnte nicht geladen werden.');
    }
  }

  const gewaehlt = rechnung
    ? rechnung.positionen
      .map(p => ({ p, menge: Number(String(mengen[p.index] ?? '').replace(',', '.')) }))
      .filter(x => Number.isFinite(x.menge) && x.menge > 0)
    : [];
  const zuviel = gewaehlt.filter(x => x.menge > x.p.retournierbar);
  const gutschrift = gewaehlt.reduce((s, x) => s + x.menge * x.p.einzelpreis, 0);
  const absendbar = gewaehlt.length > 0 && zuviel.length === 0 && !sendet;

  async function absenden(e: React.FormEvent) {
    e.preventDefault();
    if (!rechnung || !absendbar) return;
    setSendet(true);
    setFehler(null);
    try {
      await api('/returns', {
        methode: 'POST',
        daten: {
          rechnungId: rechnung.id,
          positionen: gewaehlt.map(x => ({ index: x.p.index, menge: x.menge })),
          grund,
          einlagern,
        },
      });
      await fertig();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Retoure konnte nicht gebucht werden.');
    } finally { setSendet(false); }
  }

  const schritt = !kundeId ? 1 : !rechnung ? 2 : 3;

  return (
    <Karte klasse="border-marke-300 dark:border-marke-800">
      <KartenKopf
        titel={t('retouren.erfassen')}
        hinweis={`Schritt ${schritt} von 3 · ${schritt === 1 ? 'Kunde' : schritt === 2 ? 'Rechnung' : 'Positionen'}`}
        aktion={<Knopf onClick={abbrechen}>{t('aktion.abbrechen')}</Knopf>}
      />

      <form onSubmit={absenden} className="space-y-5 p-5">
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

        {/* Schritt 1 — Kunde */}
        <Auswahl label={t('retouren.schritt1')} value={kundeId} onChange={e => void kundeWaehlen(e.target.value)}>
          <option value="">{t('retouren.kundeWaehlen')}</option>
          {(kunden ?? []).map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
        </Auswahl>

        {/* Schritt 2 — Rechnung */}
        {kundeId && (
          rechnungen === null ? <div className="py-4"><Spinner klein /></div>
            : rechnungen.length === 0 ? (
              <Hinweis art="warnung">
                Für diesen Kunden gibt es keine gestellte Rechnung. Retouren sind nur zu
                gestellten oder bezahlten Rechnungen möglich — Entwürfe und Barverkäufe nicht.
              </Hinweis>
            ) : (
              <div>
                <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">{t('retouren.schritt2')}</p>
                <ul className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
                  {rechnungen.map(r => {
                    const aktiv = rechnung?.id === r.id;
                    return (
                      <li key={r.id}>
                        <button
                          type="button"
                          disabled={r.vollstaendigRetourniert}
                          onClick={() => void rechnungWaehlen(r)}
                          className={`flex w-full items-center gap-3 px-4 py-3 text-start transition
                            ${aktiv ? 'bg-marke-50 dark:bg-marke-950' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'}
                            ${r.vollstaendigRetourniert ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {r.nummer || 'ohne Nummer'} · {euro(r.brutto)}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {datum(r.datum)} · {r.positionen.length} Positionen
                              {r.vollstaendigRetourniert ? ' · vollständig retourniert' : ''}
                            </p>
                          </div>
                          {aktiv && <span className="text-marke-600 dark:text-marke-400">✓</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )
        )}

        {/* Schritt 3 — Positionen */}
        {rechnung && (
          <div className="space-y-4">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {t('retouren.schritt3')}
            </p>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-start text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400">
                    <th className="pb-2 text-start font-medium">{t('feld.artikel')}</th>
                    <th className="pb-2 text-end font-medium">{t('retouren.berechnet')}</th>
                    <th className="pb-2 text-end font-medium">{t('status.offen')}</th>
                    <th className="pb-2 text-end font-medium">{t('feld.einzelpreis')}</th>
                    <th className="pb-2 text-end font-medium">{t('retouren.zurueck')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {rechnung.positionen.map(p => {
                    const wert = mengen[p.index] ?? '';
                    const zahl = Number(String(wert).replace(',', '.'));
                    const ueber = Number.isFinite(zahl) && zahl > p.retournierbar;
                    return (
                      <tr key={p.index} className={p.retournierbar === 0 ? 'opacity-50' : ''}>
                        <td className="py-2 pe-3 text-slate-900 dark:text-slate-100">{p.bezeichnung}</td>
                        <td className="py-2 text-end text-slate-500 dark:text-slate-400">{menge(p.menge, einheitAnzeige(p.einheit, t))}</td>
                        <td className="py-2 text-end text-slate-500 dark:text-slate-400">{menge(p.retournierbar, einheitAnzeige(p.einheit, t))}</td>
                        <td className="py-2 text-end text-slate-500 dark:text-slate-400">{preis(p.einzelpreis)}</td>
                        <td className="py-2 ps-3 text-end">
                          <div className="flex items-center justify-end gap-1.5">
                            <input
                              type="number" min="0" max={p.retournierbar} step="any"
                              disabled={p.retournierbar === 0}
                              value={wert}
                              onChange={e => setMengen(m => ({ ...m, [p.index]: e.target.value }))}
                              className={`w-20 rounded-lg border px-2 py-1 text-end text-sm
                                ${ueber
                                  ? 'border-rose-400 bg-rose-50 dark:border-rose-600 dark:bg-rose-950'
                                  : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-900'}
                                text-slate-900 disabled:opacity-40 dark:text-slate-100`}
                            />
                            <button
                              type="button"
                              disabled={p.retournierbar === 0}
                              onClick={() => setMengen(m => ({ ...m, [p.index]: String(p.retournierbar) }))}
                              className="rounded-lg border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                            >
                              alle
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {zuviel.length > 0 && (
              <Hinweis art="fehler">
                Es kann nur zurückkommen, was berechnet wurde:
                {' '}{zuviel.map(x => `${x.p.bezeichnung} (max. ${x.p.retournierbar})`).join(', ')}.
              </Hinweis>
            )}

            <Feld label={t('retouren.grund')} value={grund} maxLength={500}
                  placeholder={t('retouren.grundBeispiel')}
                  onChange={e => setGrund(e.target.value)} />

            <label className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-300">
              <input type="checkbox" checked={einlagern} onChange={e => setEinlagern(e.target.checked)}
                     className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600" />
              <span>
                {t('retouren.einlagern')}
                <span className="block text-xs text-slate-500 dark:text-slate-400">
                  Abwählen bei Defekt oder Entsorgung — dann bleibt der Bestand unverändert.
                </span>
              </span>
            </label>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-700">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('retouren.gutschrift')}</p>
                <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{euro(gutschrift)}</p>
              </div>
              <Knopf type="submit" variante="primaer" laedt={sendet} disabled={!absendbar}>
                Retoure buchen
              </Knopf>
            </div>
          </div>
        )}
      </form>
    </Karte>
  );
}
